import type { AgentDto } from '@/types/gtm-agents';
import type { SidebarNavAccess } from '../use-sidebar-nav-access.hook';
import {
  isSidebarPageId,
  pinnableDestinations,
  type SidebarDestination,
  type SidebarPageId,
} from './sidebar-destinations';

/** Something the sidebar can pin: a page, an agent, or a chat. */
export type SidebarRef =
  | { kind: 'page'; id: SidebarPageId }
  | { kind: 'agent'; id: string }
  | { kind: 'chat'; id: string };

/** The tile grid, then the rows under it, each in the user's order. */
export interface SidebarLayout {
  favorites: SidebarRef[];
  pinned: SidebarRef[];
}

export type SidebarSection = keyof SidebarLayout;

/** The user preference that holds every organization's layout. */
export const SIDEBAR_LAYOUT_PREFERENCE = 'sidebar_layout_v1';

/**
 * The version every layout is saved with. Version 5 gives every layout the
 * default pages again, all nine as tiles (version 4 had Home and Lead Lists as
 * rows); it places every page, so what versions 2 (Agents a tile) and 3
 * (Settings out) did to the pages no longer applies. A layout saved before
 * version 2 has no version.
 */
export const LAYOUT_VERSION = 5;

/** A layout as it is saved, marked with its version. */
export interface SavedSidebarLayout extends SidebarLayout {
  version: number;
}

/** A saved layout keeps at most this many refs in each section. */
const SECTION_LIMIT = 100;

/** A ref as one string, unique across kinds. */
export function refKey(ref: SidebarRef): string {
  return `${ref.kind}:${ref.id}`;
}

export function sameRef(a: SidebarRef, b: SidebarRef): boolean {
  return a.kind === b.kind && a.id === b.id;
}

const page = (id: SidebarPageId): SidebarRef => ({ kind: 'page', id });

/** The pages every sidebar starts with as rows: none, every page is a tile. */
export const DEFAULT_PAGE_ROWS: readonly SidebarRef[] = [];

/** The pages every sidebar starts with as tiles, three to a row. */
export const DEFAULT_PAGE_TILES: readonly SidebarRef[] = [
  page('home'),
  page('find-people'),
  page('agents'),
  page('lead-lists'),
  page('sequences'),
  page('inbox'),
  page('tasks'),
  page('integrations'),
  page('reports'),
];

/**
 * The layout a user starts with: every page as a tile, then the agents marked
 * favorite as rows. Settings is the footer's gear, and People and Companies are
 * tabs on Lead Lists; the pages this user cannot see are left out when the
 * sidebar renders.
 */
export function defaultLayout(
  agents: readonly Pick<AgentDto, 'id' | 'isFavorite' | 'archivedAt'>[]
): SidebarLayout {
  return {
    favorites: [...DEFAULT_PAGE_TILES],
    pinned: [
      ...DEFAULT_PAGE_ROWS,
      ...agents
        .filter((agent) => agent.isFavorite && !agent.archivedAt)
        .map((agent): SidebarRef => ({ kind: 'agent', id: agent.id })),
    ],
  };
}

function parseRef(raw: unknown): SidebarRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const { kind, id } = raw as { kind?: unknown; id?: unknown };
  if (typeof id !== 'string' || !id) return null;
  if (kind === 'page') return isSidebarPageId(id) ? { kind, id } : null;
  if (kind === 'agent' || kind === 'chat') return { kind, id };
  return null;
}

const notPages = (refs: SidebarRef[]) =>
  refs.filter((ref) => ref.kind !== 'page');

/**
 * Version 5: every layout takes the default pages in the default order, one
 * its user arranged as well: every page a tile; an unpinned page comes back,
 * and Settings, People and Companies leave. Pinned agents and chats stay where
 * they were.
 */
function adoptPageTiles(layout: SidebarLayout): SidebarLayout {
  const tiles = [...DEFAULT_PAGE_TILES, ...notPages(layout.favorites)];
  // Each section keeps the SECTION_LIMIT a read does, so the saved layout reads
  // back the same: tiles past it go to the top of the rows.
  return {
    favorites: tiles.slice(0, SECTION_LIMIT),
    pinned: [
      ...DEFAULT_PAGE_ROWS,
      ...tiles.slice(SECTION_LIMIT),
      ...notPages(layout.pinned),
    ].slice(0, SECTION_LIMIT),
  };
}

/** Each version's upgrade, in order. */
const UPGRADES: readonly {
  version: number;
  upgrade: (layout: SidebarLayout) => SidebarLayout;
}[] = [{ version: 5, upgrade: adoptPageTiles }];

/**
 * A saved layout, or null when there is none to read. Refs the sidebar does
 * not know (a page it no longer has, such as the old Extension row), and a
 * ref saved twice, are dropped. A layout saved before this version, or with
 * none, takes each upgrade since each time it is read, until a change saves
 * it with this version.
 */
export function parseLayout(raw: unknown): SidebarLayout | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { favorites, pinned, version } = raw as {
    favorites?: unknown;
    pinned?: unknown;
    version?: unknown;
  };
  if (!Array.isArray(favorites) && !Array.isArray(pinned)) return null;
  const seen = new Set<string>();
  const section = (list: unknown): SidebarRef[] => {
    if (!Array.isArray(list)) return [];
    const refs: SidebarRef[] = [];
    for (const item of list) {
      const ref = parseRef(item);
      if (!ref || seen.has(refKey(ref))) continue;
      seen.add(refKey(ref));
      refs.push(ref);
      if (refs.length === SECTION_LIMIT) break;
    }
    return refs;
  };
  const layout = { favorites: section(favorites), pinned: section(pinned) };
  // A layout with no version was saved before version 2.
  const savedWith = typeof version === 'number' ? version : 1;
  return UPGRADES.reduce(
    (next, step) => (savedWith < step.version ? step.upgrade(next) : next),
    layout
  );
}

/** A layout as it is saved: marked with this version, so it upgrades once. */
export function serializeLayout(layout: SidebarLayout): SavedSidebarLayout {
  return {
    version: LAYOUT_VERSION,
    favorites: layout.favorites,
    pinned: layout.pinned,
  };
}

/** Every organization's saved layout, by organization id. */
export function parseLayoutMap(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/** Where a ref sits, or null when it is not pinned. */
export function findRef(
  layout: SidebarLayout,
  ref: SidebarRef
): SidebarSection | null {
  if (layout.favorites.some((item) => sameRef(item, ref))) return 'favorites';
  if (layout.pinned.some((item) => sameRef(item, ref))) return 'pinned';
  return null;
}

/** The layout without the ref, wherever it was. */
export function unpinRef(
  layout: SidebarLayout,
  ref: SidebarRef
): SidebarLayout {
  return {
    favorites: layout.favorites.filter((item) => !sameRef(item, ref)),
    pinned: layout.pinned.filter((item) => !sameRef(item, ref)),
  };
}

/**
 * Puts a ref in a section, before `before` or at the end, taking it from
 * wherever it was. Pinning, moving between sections and reordering are all
 * this one step. A `before` that is not in the section means the end.
 */
export function placeRef(
  layout: SidebarLayout,
  ref: SidebarRef,
  section: SidebarSection,
  before: SidebarRef | null = null
): SidebarLayout {
  if (before && sameRef(before, ref)) return layout;
  const without = unpinRef(layout, ref);
  const list = [...without[section]];
  const index = before ? list.findIndex((item) => sameRef(item, before)) : -1;
  list.splice(index === -1 ? list.length : index, 0, ref);
  return { ...without, [section]: list };
}

/** Pins a ref at the end of the rows; one already pinned stays where it is. */
export function pinRef(layout: SidebarLayout, ref: SidebarRef): SidebarLayout {
  return findRef(layout, ref) ? layout : placeRef(layout, ref, 'pinned');
}

/**
 * The pages a user can add back: the ones this user can pin that neither
 * section holds, in registry order. Settings is never one.
 */
export function missingPages(
  layout: SidebarLayout,
  access: SidebarNavAccess
): SidebarDestination[] {
  const held = new Set([...layout.favorites, ...layout.pinned].map(refKey));
  return pinnableDestinations(access).filter(
    (destination) => !held.has(refKey(page(destination.id)))
  );
}

/** The chats the layout pins, in order. */
export function pinnedChatIds(layout: SidebarLayout): string[] {
  return [...layout.favorites, ...layout.pinned]
    .filter((ref) => ref.kind === 'chat')
    .map((ref) => ref.id);
}

export function layoutsEqual(a: SidebarLayout, b: SidebarLayout): boolean {
  const same = (x: SidebarRef[], y: SidebarRef[]) =>
    x.length === y.length && x.every((ref, i) => sameRef(ref, y[i]));
  return same(a.favorites, b.favorites) && same(a.pinned, b.pinned);
}

/** Show pages as rows: the page tiles move to the top of the rows, in order. */
export function pagesToRows(layout: SidebarLayout): SidebarLayout {
  return {
    favorites: notPages(layout.favorites),
    pinned: [
      ...layout.favorites.filter((ref) => ref.kind === 'page'),
      ...layout.pinned,
    ],
  };
}

/** Show pages as tiles: the page rows move to the end of the tiles, in order. */
export function pagesToTiles(layout: SidebarLayout): SidebarLayout {
  return {
    favorites: [
      ...layout.favorites,
      ...layout.pinned.filter((ref) => ref.kind === 'page'),
    ],
    pinned: notPages(layout.pinned),
  };
}

/**
 * Which way the pages can switch: to rows while any page is a tile, else to
 * tiles while any page is a row; null with no page pinned at all.
 */
export function pagesSwitch(layout: SidebarLayout): 'rows' | 'tiles' | null {
  if (layout.favorites.some((ref) => ref.kind === 'page')) return 'rows';
  if (layout.pinned.some((ref) => ref.kind === 'page')) return 'tiles';
  return null;
}
